import { useState, useEffect, useMemo, useCallback } from 'react';
import { ChevronDown, ChevronRight, Radio, BatteryLow, Thermometer, Signal, Pencil, X, RefreshCw } from 'lucide-react';
import { apiFetch } from '../api/client';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface Device {
  phantom_code: string;
  machine_code: string;
  point_index: number;
  point_name: string | null;
  db_id: number;
  sensor_name: string | null;
  model: string | null;
  last_data_at: string | null;
  battery_voltage: number | null;
  signal_strength: number | null;
  sensor_temperature: number | null;
  machine_name: string;
  company_name: string;
  area_name: string;
  db_name: string | null;
  axis_mapping: Record<string, string>;
}

type AxisLabel = 'H' | 'V' | 'A';
type PhysChannel = 'x' | 'y' | 'z';

interface TreeRow {
  db_id: number;
  db_name: string | null;
  company_name: string;
  area_name: string;
  machine_code: string;
  machine_name: string;
  point_index: number;
  point_name: string | null;
  device_count: number;
}

interface FilterState {
  db_id?: number;
  company_name?: string;
  area_name?: string;
  machine_code?: string;
  point_index?: number;
  label?: string;
}

type DeviceStatus = 'connected' | 'low_battery' | 'disconnected';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseLastSeen(raw: string | null): Date | null {
  if (!raw) return null;
  return new Date(raw.replace(/\//g, '-'));
}

// EI-Analytic returns -1 for battery/signal/temperature when the value is unavailable.
// Treat any non-positive value as null throughout.
function realBattery(v: number | null): number | null {
  return v !== null && v > 0 ? v : null;
}
function realTemp(c: number | null): number | null {
  return c !== null && c > -100 ? c : null;
}
function realSignal(s: number | null): number | null {
  return s !== null && s !== 0 ? s : null;
}

function deviceStatus(d: Device): DeviceStatus {
  const lastSeen = parseLastSeen(d.last_data_at);
  const daysSince = lastSeen ? (Date.now() - lastSeen.getTime()) / 86_400_000 : Infinity;
  if (daysSince > 5) return 'disconnected';
  const batt = realBattery(d.battery_voltage);
  if (batt !== null && batt < 2.6) return 'low_battery';
  return 'connected';
}

function fmtLastSeen(raw: string | null): { date: string; time: string } | null {
  if (!raw) return null;
  const d = parseLastSeen(raw);
  if (!d || isNaN(d.getTime())) return null;
  const date = d.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const time = d.toTimeString().slice(0, 8);
  return { date, time };
}

function fmtTemp(c: number | null): string {
  const v = realTemp(c);
  if (v === null) return '—';
  return `${((v * 9) / 5 + 32).toFixed(0)} °F`;
}

function fmtBattery(v: number | null): string {
  const b = realBattery(v);
  if (b === null) return '—';
  return `${b.toFixed(2)} V`;
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: DeviceStatus }) {
  const colour =
    status === 'connected' ? 'bg-green-500' :
    status === 'low_battery' ? 'bg-orange-400' :
    'bg-red-500';
  return <span className={`inline-block w-3 h-3 rounded-full flex-shrink-0 ${colour} shadow-sm`} />;
}

// ---------------------------------------------------------------------------
// Battery icon (red when critical, orange when low)
// ---------------------------------------------------------------------------

function BatteryIcon({ v }: { v: number | null }) {
  const b = realBattery(v);
  if (b === null) return null;
  const cls = b < 2.2 ? 'text-red-500' : b < 2.6 ? 'text-orange-400' : 'text-gray-400';
  return <BatteryLow className={`w-5 h-5 ${cls}`} />;
}

// ---------------------------------------------------------------------------
// Axis mapping modal
// ---------------------------------------------------------------------------

const AXIS_LABELS: AxisLabel[] = ['H', 'V', 'A'];
const CHANNEL_LABELS: Record<PhysChannel, string> = { x: 'X', y: 'Y', z: 'Z' };

function AxisMappingModal({
  device,
  onClose,
  onSaved,
}: {
  device: Device;
  onClose: () => void;
  onSaved: (phantomCode: string, mapping: Record<string, string>) => void;
}) {
  const initial = (
    device.axis_mapping && Object.keys(device.axis_mapping).length === 3
      ? device.axis_mapping
      : { x: 'H', y: 'V', z: 'A' }
  ) as Record<PhysChannel, AxisLabel>;

  const [mapping, setMapping] = useState<Record<PhysChannel, AxisLabel>>(initial as Record<PhysChannel, AxisLabel>);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const setChannel = (ch: PhysChannel, axis: AxisLabel) => {
    setMapping(prev => {
      const current = prev[ch];
      const swapCh = (Object.entries(prev) as [PhysChannel, AxisLabel][]).find(([k, v]) => v === axis && k !== ch)?.[0];
      const next = { ...prev, [ch]: axis } as Record<PhysChannel, AxisLabel>;
      if (swapCh) next[swapCh] = current;
      return next;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setErr('');
    try {
      await apiFetch(`/setup/devices/${device.phantom_code}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ axis_mapping: mapping }),
      });
      onSaved(device.phantom_code, mapping);
      onClose();
    } catch (e: any) {
      setErr(e.message ?? 'Save failed');
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div>
            <h2 className="text-base font-semibold text-gray-800">Axis Mapping</h2>
            <p className="text-xs text-gray-400 font-mono mt-0.5">{device.phantom_code}</p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-gray-500 mb-5">
          Map each physical sensor channel to its measurement axis.
        </p>

        <div className="space-y-3 mb-6">
          {(['x', 'y', 'z'] as PhysChannel[]).map(ch => (
            <div key={ch} className="flex items-center gap-3">
              <span className="w-6 text-sm font-semibold text-gray-600">{CHANNEL_LABELS[ch]}</span>
              <div className="flex gap-1">
                {AXIS_LABELS.map(axis => {
                  const active = mapping[ch] === axis;
                  return (
                    <button
                      key={axis}
                      type="button"
                      onClick={() => setChannel(ch, axis)}
                      className={`w-10 py-1.5 rounded text-sm font-semibold border transition-colors ${
                        active
                          ? 'bg-[#00D4BE] border-[#00D4BE] text-white'
                          : 'bg-white border-gray-200 text-gray-500 hover:border-[#00D4BE]/50'
                      }`}
                    >
                      {axis}
                    </button>
                  );
                })}
              </div>
              <span className="text-xs text-gray-400 ml-1">→ {mapping[ch]}</span>
            </div>
          ))}
        </div>

        {err && <p className="text-xs text-red-500 mb-3">{err}</p>}

        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-semibold bg-[#00D4BE] text-white rounded-lg hover:bg-[#00bfab] transition-colors disabled:opacity-60"
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Device card
// ---------------------------------------------------------------------------

function DeviceCard({ device, onEdit }: { device: Device; onEdit: () => void }) {
  const status = deviceStatus(device);
  const lastSeen = fmtLastSeen(device.last_data_at);

  const borderColour =
    status === 'connected' ? 'border-gray-200' :
    status === 'low_battery' ? 'border-orange-200' :
    'border-red-200';

  return (
    <div className={`bg-white rounded-xl border ${borderColour} shadow-sm mb-3 overflow-hidden`}>
      {/* Header row */}
      <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-100">
        <StatusDot status={status} />
        <Radio className="w-4 h-4 text-gray-400 flex-shrink-0" />
        <span className="font-bold text-gray-800 text-base tracking-wide">{device.phantom_code}</span>

        {/* Right side — last seen + signal + temp + battery + edit */}
        <div className="ml-auto flex items-center gap-5 text-sm text-gray-500">
          {lastSeen && (
            <div className="text-right">
              <div className="text-xs text-gray-400">{lastSeen.date}</div>
              <div className="text-xs text-gray-500 font-mono">{lastSeen.time}</div>
            </div>
          )}
          {realSignal(device.signal_strength) !== null && (
            <div className="flex items-center gap-1">
              <Signal className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{device.signal_strength} dB</span>
            </div>
          )}
          {realTemp(device.sensor_temperature) !== null && (
            <div className="flex items-center gap-1">
              <Thermometer className="w-4 h-4 text-gray-400" />
              <span className="text-xs font-medium">{fmtTemp(device.sensor_temperature)}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <BatteryIcon v={device.battery_voltage} />
            <span className={`text-xs font-medium ${
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.2 ? 'text-red-500' :
              realBattery(device.battery_voltage) !== null && realBattery(device.battery_voltage)! < 2.6 ? 'text-orange-500' :
              'text-gray-500'
            }`}>
              {fmtBattery(device.battery_voltage)}
            </span>
          </div>
          <button
            type="button"
            onClick={onEdit}
            title="Edit axis mapping"
            className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors"
          >
            <Pencil className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Info row */}
      <div className="px-5 py-3">
        <div className="flex items-center gap-8 text-sm text-gray-500 mb-3">
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Model</span>{device.model ?? '—'}</span>
          <span><span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Name</span>{device.sensor_name ?? '—'}</span>
          {Object.keys(device.axis_mapping ?? {}).length === 3 && (
            <span className="flex items-center gap-1">
              <span className="text-gray-400 text-xs uppercase tracking-wide mr-1">Axes</span>
              {(['x', 'y', 'z'] as PhysChannel[]).map((ch, i) => (
                <span key={ch} className="text-xs font-mono">
                  {i > 0 && <span className="text-gray-300 mx-0.5">·</span>}
                  <span className="text-gray-400">{ch.toUpperCase()}</span>
                  <span className="text-gray-300">→</span>
                  <span className="text-[#00D4BE] font-semibold">{(device.axis_mapping as any)[ch]}</span>
                </span>
              ))}
            </span>
          )}
        </div>

        {/* Location breadcrumb */}
        <div className="flex items-center gap-1 text-sm text-gray-500 flex-wrap">
          {[
            device.db_name,
            device.company_name,
            device.area_name,
            device.machine_name,
            device.point_name,
          ].filter(Boolean).map((part, i, arr) => (
            <span key={i} className="flex items-center gap-1">
              <span className="text-gray-600">{part}</span>
              {i < arr.length - 1 && <ChevronRight className="w-3.5 h-3.5 text-gray-300" />}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hierarchy tree (left sidebar)
// ---------------------------------------------------------------------------

interface TreeNode {
  key: string;
  label: string;
  count: number;
  filter: FilterState;
  children: TreeNode[];
}

function buildTree(rows: TreeRow[]): TreeNode[] {
  const roots: TreeNode[] = [];
  const dbMap = new Map<number, TreeNode>();
  const coMap = new Map<string, TreeNode>();
  const arMap = new Map<string, TreeNode>();
  const mMap = new Map<string, TreeNode>();

  for (const r of rows) {
    const dbKey = String(r.db_id);
    if (!dbMap.has(r.db_id)) {
      const node: TreeNode = {
        key: dbKey,
        label: r.db_name ?? `DB ${r.db_id}`,
        count: 0,
        filter: { db_id: r.db_id, label: r.db_name ?? `DB ${r.db_id}` },
        children: [],
      };
      dbMap.set(r.db_id, node);
      roots.push(node);
    }

    const coKey = `${r.db_id}|${r.company_name}`;
    if (!coMap.has(coKey)) {
      const node: TreeNode = {
        key: coKey,
        label: r.company_name,
        count: 0,
        filter: { db_id: r.db_id, company_name: r.company_name, label: r.company_name },
        children: [],
      };
      coMap.set(coKey, node);
      dbMap.get(r.db_id)!.children.push(node);
    }

    const arKey = `${coKey}|${r.area_name}`;
    if (!arMap.has(arKey)) {
      const node: TreeNode = {
        key: arKey,
        label: r.area_name,
        count: 0,
        filter: { db_id: r.db_id, company_name: r.company_name, area_name: r.area_name, label: r.area_name },
        children: [],
      };
      arMap.set(arKey, node);
      coMap.get(coKey)!.children.push(node);
    }

    const mKey = r.machine_code;
    if (!mMap.has(mKey)) {
      const node: TreeNode = {
        key: mKey,
        label: r.machine_name,
        count: 0,
        filter: { db_id: r.db_id, company_name: r.company_name, area_name: r.area_name, machine_code: r.machine_code, label: r.machine_name },
        children: [],
      };
      mMap.set(mKey, node);
      arMap.get(arKey)!.children.push(node);
    }

    // Point node
    const ptKey = `${mKey}|${r.point_index}`;
    const ptNode: TreeNode = {
      key: ptKey,
      label: r.point_name ?? `Point ${r.point_index}`,
      count: r.device_count,
      filter: { db_id: r.db_id, company_name: r.company_name, area_name: r.area_name, machine_code: r.machine_code, point_index: r.point_index, label: r.point_name ?? `Point ${r.point_index}` },
      children: [],
    };
    mMap.get(mKey)!.children.push(ptNode);

    // Roll up counts
    mMap.get(mKey)!.count += r.device_count;
    arMap.get(arKey)!.count += r.device_count;
    coMap.get(coKey)!.count += r.device_count;
    dbMap.get(r.db_id)!.count += r.device_count;
  }

  return roots;
}

function TreeNodeRow({
  node,
  depth,
  selectedKey,
  onSelect,
}: {
  node: TreeNode;
  depth: number;
  selectedKey: string | null;
  onSelect: (n: TreeNode) => void;
}) {
  const [open, setOpen] = useState(depth < 2);
  const hasChildren = node.children.length > 0;
  const isSelected = selectedKey === node.key;

  return (
    <div>
      <button
        type="button"
        onClick={() => { onSelect(node); if (hasChildren) setOpen(!open); }}
        className={`w-full flex items-center gap-1 px-2 py-1 rounded text-sm text-left transition-colors ${
          isSelected ? 'bg-[#00D4BE]/10 text-[#00D4BE] font-medium' : 'text-gray-600 hover:bg-gray-100'
        }`}
        style={{ paddingLeft: `${0.5 + depth * 0.875}rem` }}
      >
        {hasChildren ? (
          open
            ? <ChevronDown className="w-3.5 h-3.5 flex-shrink-0" />
            : <ChevronRight className="w-3.5 h-3.5 flex-shrink-0" />
        ) : (
          <span className="w-3.5" />
        )}
        <span className="flex-1 truncate">{node.label}</span>
        <span className={`text-xs rounded-full px-1.5 py-0.5 flex-shrink-0 ${
          isSelected ? 'bg-[#00D4BE]/20 text-[#00D4BE]' : 'bg-gray-100 text-gray-400'
        }`}>
          {node.count}
        </span>
      </button>
      {open && hasChildren && (
        <div>
          {node.children.map(child => (
            <TreeNodeRow
              key={child.key}
              node={child}
              depth={depth + 1}
              selectedKey={selectedKey}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
}

type StatusFilter = 'all' | 'connected' | 'low_battery' | 'disconnected';

// ---------------------------------------------------------------------------
// Status summary bar
// ---------------------------------------------------------------------------

function StatusBar({
  devices,
  statusFilter,
  onStatusFilter,
}: {
  devices: Device[];
  statusFilter: StatusFilter;
  onStatusFilter: (s: StatusFilter) => void;
}) {
  const connected = devices.filter(d => deviceStatus(d) === 'connected').length;
  const lowBatt = devices.filter(d => deviceStatus(d) === 'low_battery').length;
  const disc = devices.filter(d => deviceStatus(d) === 'disconnected').length;

  const pill = (
    status: StatusFilter,
    count: number,
    dotClass: string,
    label: string,
  ) => {
    const active = statusFilter === status;
    return (
      <button
        type="button"
        onClick={() => onStatusFilter(active ? 'all' : status)}
        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors border ${
          active
            ? 'border-transparent shadow-sm ' +
              (status === 'connected'    ? 'bg-green-100 text-green-700' :
               status === 'low_battery' ? 'bg-orange-100 text-orange-700' :
                                          'bg-red-100 text-red-700')
            : 'border-gray-200 bg-white text-gray-600 hover:bg-gray-50'
        }`}
      >
        <span className={`w-2.5 h-2.5 rounded-full inline-block flex-shrink-0 ${dotClass}`} />
        {count} {label}
      </button>
    );
  };

  return (
    <div className="flex items-center gap-2 text-sm mb-4">
      {pill('connected',    connected, 'bg-green-500',  'Connected')}
      {pill('low_battery',  lowBatt,   'bg-orange-400', 'Low battery')}
      {pill('disconnected', disc,      'bg-red-500',    'Disconnected')}
      <span className="text-gray-400 ml-auto text-xs">Total: {devices.length}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function ReviewDevices() {
  const [allDevices, setAllDevices] = useState<Device[]>([]);
  const [treeRows, setTreeRows] = useState<TreeRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState<FilterState | null>(null);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [editingDevice, setEditingDevice] = useState<Device | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  // Full load — initial mount or manual refresh (also reloads tree)
  const loadAll = useCallback((showSpinner = false) => {
    if (showSpinner) setRefreshing(true);
    else setLoading(true);
    Promise.all([
      apiFetch('/setup/devices'),
      apiFetch('/setup/device-tree'),
    ])
      .then(([devs, tree]: [Device[], TreeRow[]]) => {
        setAllDevices(devs);
        setTreeRows(tree);
        setLastUpdated(new Date());
        setError('');
      })
      .catch(() => setError('Failed to load devices.'))
      .finally(() => { setLoading(false); setRefreshing(false); });
  }, []);

  // Silent background refresh — devices only, no spinner
  const refreshDevices = useCallback(() => {
    apiFetch('/setup/devices')
      .then((devs: Device[]) => {
        setAllDevices(devs);
        setLastUpdated(new Date());
      })
      .catch(() => {});
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    const id = setInterval(refreshDevices, 60_000);
    return () => clearInterval(id);
  }, [refreshDevices]);

  const tree = useMemo(() => buildTree(treeRows), [treeRows]);

  const filtered = useMemo(() => {
    let list = allDevices;
    if (filter) {
      if (filter.db_id !== undefined) list = list.filter(d => d.db_id === filter.db_id);
      if (filter.company_name) list = list.filter(d => d.company_name === filter.company_name);
      if (filter.area_name) list = list.filter(d => d.area_name === filter.area_name);
      if (filter.machine_code) list = list.filter(d => d.machine_code === filter.machine_code);
      if (filter.point_index !== undefined) list = list.filter(d => d.point_index === filter.point_index);
    }
    if (statusFilter !== 'all') {
      list = list.filter(d => deviceStatus(d) === statusFilter);
    }
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(d =>
        d.phantom_code.includes(q) ||
        (d.sensor_name ?? '').toLowerCase().includes(q) ||
        d.machine_name.toLowerCase().includes(q) ||
        (d.point_name ?? '').toLowerCase().includes(q)
      );
    }
    // When filtering by low battery, sort ascending by voltage so worst are first
    if (statusFilter === 'low_battery') {
      list = [...list].sort((a, b) => (a.battery_voltage ?? 99) - (b.battery_voltage ?? 99));
    }
    return list;
  }, [allDevices, filter, statusFilter, search]);

  const handleNodeSelect = (node: TreeNode) => {
    if (selectedKey === node.key) {
      setSelectedKey(null);
      setFilter(null);
    } else {
      setSelectedKey(node.key);
      setFilter(node.filter);
    }
  };

  const handleAxisMappingSaved = (phantomCode: string, mapping: Record<string, string>) => {
    setAllDevices(prev =>
      prev.map(d => d.phantom_code === phantomCode ? { ...d, axis_mapping: mapping } : d)
    );
  };

  return (
    <>
    {editingDevice && (
      <AxisMappingModal
        device={editingDevice}
        onClose={() => setEditingDevice(null)}
        onSaved={handleAxisMappingSaved}
      />
    )}
    <div className="flex gap-5 h-full min-h-0">
      {/* Left sidebar — hierarchy tree */}
      <div className="w-64 flex-shrink-0 bg-white rounded-xl border border-gray-200 shadow-sm flex flex-col">
        <div className="px-4 py-3 border-b border-gray-100">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Filter by hierarchy</p>
        </div>
        <div className="flex-1 overflow-y-auto px-2 py-2">
          {/* All devices root */}
          <button
            type="button"
            onClick={() => { setSelectedKey(null); setFilter(null); }}
            className={`w-full flex items-center justify-between px-2 py-1.5 rounded text-sm text-left mb-1 transition-colors ${
              selectedKey === null ? 'bg-[#00D4BE]/10 text-[#00D4BE] font-medium' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            <span>All Devices</span>
            <span className={`text-xs rounded-full px-1.5 py-0.5 ${selectedKey === null ? 'bg-[#00D4BE]/20 text-[#00D4BE]' : 'bg-gray-100 text-gray-400'}`}>
              {allDevices.length}
            </span>
          </button>
          {tree.map(node => (
            <TreeNodeRow
              key={node.key}
              node={node}
              depth={0}
              selectedKey={selectedKey}
              onSelect={handleNodeSelect}
            />
          ))}
        </div>
      </div>

      {/* Right content */}
      <div className="flex-1 min-w-0 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-800">
              {filter?.label ?? 'Phantom Devices'}
            </h1>
            {filter && (
              <button
                type="button"
                onClick={() => { setSelectedKey(null); setFilter(null); }}
                className="text-xs text-[#00D4BE] hover:underline mt-0.5"
              >
                ← All devices
              </button>
            )}
          </div>
          <div className="flex items-center gap-3">
            {lastUpdated && (
              <span className="text-xs text-gray-400">
                Updated {lastUpdated.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
              </span>
            )}
            <button
              type="button"
              onClick={() => loadAll(true)}
              disabled={refreshing}
              title="Refresh"
              className="p-1.5 rounded-lg text-gray-400 hover:text-[#00D4BE] hover:bg-[#00D4BE]/10 transition-colors disabled:opacity-40"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} />
            </button>
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search by code, name, machine…"
              className="w-64 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg focus:outline-none focus:border-[#00D4BE]/60 transition-colors"
            />
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center h-32 text-gray-400 text-sm">Loading devices…</div>
        )}
        {error && (
          <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded-lg px-4 py-3">{error}</div>
        )}

        {!loading && !error && (
          <>
            <StatusBar
              devices={allDevices.filter(d => {
                if (!filter) return true;
                if (filter.db_id !== undefined && d.db_id !== filter.db_id) return false;
                if (filter.company_name && d.company_name !== filter.company_name) return false;
                if (filter.area_name && d.area_name !== filter.area_name) return false;
                if (filter.machine_code && d.machine_code !== filter.machine_code) return false;
                if (filter.point_index !== undefined && d.point_index !== filter.point_index) return false;
                return true;
              })}
              statusFilter={statusFilter}
              onStatusFilter={setStatusFilter}
            />
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="flex items-center justify-center h-32 text-gray-400 text-sm">No devices match the current filter.</div>
              )}
              {filtered.map(d => (
                <DeviceCard
                  key={`${d.phantom_code}-${d.point_index}`}
                  device={d}
                  onEdit={() => setEditingDevice(d)}
                />
              ))}
            </div>
          </>
        )}
      </div>
    </div>
    </>
  );
}
